import { useParams, Link } from "react-router-dom";
import { categories, articles } from "@/mocks/articles";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-foreground-950 mb-2">
            分类未找到
          </h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  const articleImages: Record<string, string> = {
    "article-intro-01":
      "https://readdy.ai/api/search-image?query=Clean%20modern%20minimalist%20workspace%20with%20laptop%20on%20wooden%20desk%2C%20natural%20light%2C%20professional%20atmosphere%2C%20editorial%20photography%2C%20warm%20cream%20tones%2C%20calm%20and%20focused%20mood%2C%20soft%20shadows&width=800&height=450&seq=article-intro-01&orientation=landscape",
    "article-install-02":
      "https://readdy.ai/api/search-image?query=Smartphone%20with%20app%20installation%20screen%2C%20clean%20minimal%20setup%2C%20warm%20lighting%2C%20modern%20flat%20lay%2C%20tech%20lifestyle%2C%20professional%20photography%2C%20soft%20cream%20background&width=800&height=450&seq=article-install-02&orientation=landscape",
    "article-fake-03":
      "https://readdy.ai/api/search-image?query=Magnifying%20glass%20over%20digital%20screen%2C%20cybersecurity%20concept%2C%20minimalist%20design%2C%20warm%20professional%20atmosphere%2C%20abstract%20tech%20patterns%2C%20clean%20editorial%20style&width=800&height=450&seq=article-fake-03&orientation=landscape",
    "article-security-04":
      "https://readdy.ai/api/search-image?query=Digital%20security%20shield%20concept%2C%20padlock%20on%20abstract%20background%2C%20warm%20golden%20tones%2C%20minimalist%20tech%20aesthetic%2C%20professional%20data%20protection%20theme%2C%20clean%20modern%20design&width=800&height=450&seq=article-security-04&orientation=landscape",
    "article-faq-05":
      "https://readdy.ai/api/search-image?query=FAQ%20help%20center%20concept%2C%20organized%20documents%20and%20icons%2C%20warm%20professional%20atmosphere%2C%20clean%20minimalist%20workspace%2C%20editorial%20style%2C%20soft%20natural%20light&width=800&height=450&seq=article-faq-05&orientation=landscape",
    "article-compare-06":
      "https://readdy.ai/api/search-image?query=Comparison%20split%20screen%20concept%2C%20two%20smartphones%20side%20by%20side%2C%20minimalist%20tech%20comparison%2C%20warm%20neutral%20tones%2C%20clean%20professional%20photography%2C%20soft%20lighting&width=800&height=450&seq=article-compare-06&orientation=landscape",
    "article-privacy-07":
      "https://readdy.ai/api/search-image?query=Privacy%20protection%20concept%2C%20document%20with%20shield%20icon%2C%20warm%20professional%20atmosphere%2C%20minimalist%20legal%20theme%2C%20clean%20modern%20design%2C%20soft%20lighting&width=800&height=450&seq=article-privacy-07&orientation=landscape",
    "article-streamer-08":
      "https://readdy.ai/api/search-image?query=Professional%20streaming%20setup%20with%20microphone%20and%20lighting%2C%20minimalist%20content%20creator%20workspace%2C%20warm%20atmosphere%2C%20modern%20tech%20environment%2C%20clean%20editorial%20style&width=800&height=450&seq=article-streamer-08&orientation=landscape",
    "article-trend-09":
      "https://readdy.ai/api/search-image?query=Data%20analytics%20dashboard%20with%20charts%20and%20graphs%2C%20minimalist%20business%20intelligence%20concept%2C%20warm%20professional%20atmosphere%2C%20clean%20modern%20design%2C%20soft%20lighting&width=800&height=450&seq=article-trend-09&orientation=landscape",
    "article-support-10":
      "https://readdy.ai/api/search-image?query=Customer%20support%20headset%20on%20desk%2C%20help%20desk%20concept%2C%20minimalist%20office%20scene%2C%20warm%20professional%20atmosphere%2C%20clean%20photography%2C%20soft%20lighting&width=800&height=450&seq=article-support-10&orientation=landscape",
  };

  return (
    <>
      <SEOHead
        title={category.name}
        description={category.description}
        keywords={`91直播平台, ${category.name}, 91直播${category.name}`}
      />
      <div className="pt-16">
        <Breadcrumb items={[{ label: category.name }]} />

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="mb-10">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  {category.name}
                </h1>
                <p className="text-sm md:text-base text-foreground-600">
                  {category.description}
                </p>
              </div>

              {/* Category Tabs */}
              <div className="flex items-center gap-2 flex-wrap mb-8">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      c.slug === slug
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-700"
                    }`}
                  >
                    {c.name}
                  </Link>
                ))}
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {categoryArticles.map((article) => {
                    const imgSrc = articleImages[article.imageSeq] || articleImages["article-intro-01"];
                    return (
                      <Link
                        key={article.slug}
                        to={`/article/${article.slug}`}
                        className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-200 flex flex-col sm:flex-row"
                      >
                        <div className="relative w-full sm:w-48 h-40 sm:h-auto overflow-hidden flex-shrink-0">
                          <img
                            src={imgSrc}
                            alt={article.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4 flex flex-col flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-foreground-500 whitespace-nowrap">
                              {article.date}
                            </span>
                            <span className="text-xs text-foreground-400">·</span>
                            <span className="text-xs text-foreground-500 whitespace-nowrap">
                              {article.readTime}
                            </span>
                          </div>
                          <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                            {article.title}
                          </h3>
                          <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                            {article.excerpt}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">
                    该分类下暂无文章
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}